import axios from 'axios';
import * as Sentry from '@sentry/react';

const API_URL = process.env.REACT_APP_API_URL;
if (!API_URL) {
    console.error('API_URL is not defined! Make sure REACT_APP_API_URL environment variable is set.');
}

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,  // CRITICAL: This sends cookies (including sessionid cookie) with all requests
    headers: {
        'Content-Type': 'application/json',
    },
    xsrfCookieName: 'csrftoken',  // Match Django's default CSRF cookie name
    xsrfHeaderName: 'X-CSRFTOKEN',
});

// Axios configured with withCredentials for cookie-based authentication

// Add request interceptor to ensure CSRF token is set
api.interceptors.request.use(async (config) => {
    // Skip CSRF check for CSRF endpoint itself and GET requests
    if (config.url === '/api/auth/csrf/' || config.method === 'get') {
        return config;
    }

    // Handle FormData - don't set Content-Type, let browser set it with boundary
    if (config.data instanceof FormData) {
        // Remove Content-Type header for FormData - browser will set it correctly
        delete config.headers['Content-Type'];
    }

    // Get CSRF token from cookie (try both secure and regular names)
    let csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken=') || row.startsWith('__Secure-csrftoken='))
        ?.split('=')[1];

    // If no token, try to get one
    if (!csrfToken) {
        try {
            const response = await api.get('/api/auth/csrf/');
            // Use token from response instead of cookie
            csrfToken = response.data.csrfToken;

            if (!csrfToken) {
                console.error('No CSRF token in response');
            }
        } catch (error) {
            console.error('Failed to get CSRF token:', error);
        }
    }

    if (csrfToken) {
        // Set both header variations to ensure one works
        config.headers['X-CSRFTOKEN'] = csrfToken;
        config.headers['X-CSRFToken'] = csrfToken;
    } else {
        console.error('No CSRF token available for request:', config.url);
    }

    // CSRF token set in headers

    return config;
});

// Request interceptor for CSRF token handling (no debug logging in production)

// Add response interceptor for handling auth errors and capturing to Sentry
api.interceptors.response.use(
    response => response,
    error => {
        // Capture all API errors to Sentry (only if Sentry is initialized)
        if (process.env.REACT_APP_SENTRY_ENABLED === 'true') {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'api_error',
                    apiUrl: error.config?.url,
                    httpMethod: error.config?.method?.toUpperCase(),
                },
                contexts: {
                    request: {
                        method: error.config?.method?.toUpperCase(),
                        url: error.config?.url,
                        baseURL: error.config?.baseURL,
                        headers: error.config?.headers,
                    },
                    response: {
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        data: error.response?.data,
                    },
                },
                extra: {
                    requestData: error.config?.data,
                    params: error.config?.params,
                },
            });
        }

        // Redirect to login only on 401 (unauthenticated).
        // IMPORTANT: do NOT redirect on 403. We use 403 for valid gameplay constraints
        // (e.g., "only host can start") and read-only anonymous flows. Redirecting on 403
        // causes confusing "random" logouts during gameplay and admin/user actions.
        if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
            window.location.href = '/login?next=' + encodeURIComponent(window.location.pathname);
        }
        return Promise.reject(error);
    }
);

// Service for GameMap (strategy planning maps)
// NOTE: This is different from GeoguesserMap. GeoguesserMap uses geoguesserService.getMaps()
export const mapService = {
    getAll: (gameId) => {
        const params = gameId ? { game: gameId } : {};
        // Returns GameMap objects with 'image' field (not 'map_image')
        return api.get('/api/maps/maps/', { params });
    },
    getById: (id) => api.get(`/api/maps/maps/${id}/`),
    create: (data) => api.post('/api/maps/maps/', data),
    update: (id, data) => api.put(`/api/maps/maps/${id}/`, data),
    delete: (id) => api.delete(`/api/maps/maps/${id}/`),
    getOperators: (gameId, side = 'all') => {
        const params = {};
        if (gameId) params.game = gameId;
        if (side && side !== 'all') params.side = side;
        return api.get('/api/maps/operators/', { params });
    },
};

export const gameService = {
    getAll: (options = {}) => {
        const opts = typeof options === 'boolean' ? { includeInactive: options } : options;
        const params = {};
        if (opts.includeInactive) params.include_inactive = 'true';
        if (opts.hasMaps) params.has_maps = 'true';
        return api.get('/api/games/games/', { params });
    },
    getById: (id) => api.get(`/api/games/games/${id}/`),
    create: (data) => api.post('/api/games/games/', data),
    update: (id, data) => api.put(`/api/games/games/${id}/`, data),
    delete: (id) => api.delete(`/api/games/games/${id}/`),
};

export const strategyService = {
    getAll: (mapId, params = {}) => {
        const queryParams = { ...params };
        if (mapId) queryParams.map = mapId;
        return api.get('/api/maps/strategies/', { params: queryParams });
    },
    getById: (id) => api.get(`/api/maps/strategies/${id}/`),
    create: (data) => api.post('/api/maps/strategies/', data),
    update: (id, data) => api.put(`/api/maps/strategies/${id}/`, data),
    delete: (id) => api.delete(`/api/maps/strategies/${id}/`),
    // Forking methods
    fork: (id, data = {}) => api.post(`/api/maps/strategies/${id}/fork/`, data),
    toggleForking: (id) => api.post(`/api/maps/strategies/${id}/toggle_forking/`),
    // Versioning methods
    getVersions: (id) => api.get(`/api/maps/strategies/${id}/versions/`),
    getVersion: (id, major, minor, patch) => api.get(`/api/maps/strategies/${id}/versions/${major}/${minor}/${patch}/`),
    saveDraft: (id, data) => api.post(`/api/maps/strategies/${id}/save_draft/`, data),
    publishDraft: (id, data) => api.post(`/api/maps/strategies/${id}/publish_draft/`, data),
    publishChanges: (id, data) => api.post(`/api/maps/strategies/${id}/publish_changes/`, data),
    // Draft methods
    getDrafts: (id) => api.get(`/api/maps/strategies/${id}/drafts/`),
    createDraft: (id, data) => api.post(`/api/maps/strategies/${id}/drafts/`, data),
    submitDraft: (strategyId, draftId) => api.post(`/api/maps/strategies/${strategyId}/drafts/${draftId}/submit/`),
    approveDraft: (id, draftId, data) => api.post(`/api/maps/strategies/${id}/drafts/${draftId}/approve/`, data),
    rejectDraft: (id, draftId, data) => api.post(`/api/maps/strategies/${id}/drafts/${draftId}/reject/`, data),
    // Comment methods
    getComments: (id) => api.get(`/api/maps/strategies/${id}/comments/`),
    createComment: (id, data) => api.post(`/api/maps/strategies/${id}/comments/`, data),
    updateComment: (id, commentId, data) => api.patch(`/api/maps/strategies/${id}/comments/${commentId}/`, data),
    deleteComment: (id, commentId) => api.delete(`/api/maps/strategies/${id}/comments/${commentId}/`),
    // Rating methods
    getRatings: (id) => api.get(`/api/maps/strategies/${id}/ratings/`),
    rate: (id, rating) => api.post(`/api/maps/strategies/${id}/ratings/`, { rating }),
    updateRating: (id, ratingId, rating) => api.patch(`/api/maps/strategies/${id}/ratings/${ratingId}/`, { rating }),
    deleteRating: (id, ratingId) => api.delete(`/api/maps/strategies/${id}/ratings/${ratingId}/`),
    // Contributor methods
    getContributors: (id) => api.get(`/api/maps/strategies/${id}/contributors/`),
    addContributor: (id, data) => api.post(`/api/maps/strategies/${id}/contributors/`, data),
    updateContributorRole: (id, userId, role) => api.patch(`/api/maps/strategies/${id}/contributors/${userId}/update_role/`, { role }),
    removeContributor: (id, userId) => api.delete(`/api/maps/strategies/${id}/contributors/${userId}/`),
    generateInviteLink: (id) => api.post(`/api/maps/strategies/${id}/contributors/invite/`),
    acceptInvitation: (id, token) => api.post(`/api/maps/strategies/${id}/contributors/accept/`, { invite_token: token }),
    getInvitations: (id) => api.get(`/api/maps/strategies/${id}/contributors/invitations/`),
};

export const drawingElementService = {
    getAll: (strategyId) => api.get(`/api/maps/strategies/${strategyId}/elements/`),
    create: (elementData) => {
        console.log('API creating element with data:', elementData);
        try {
            let payload;
            if (elementData.element_type === 'operator') {
                payload = {
                    strategy: elementData.strategy,
                    element_type: elementData.element_type,
                    data: elementData.data
                };
            } else if (elementData.element_type === 'line') {
                payload = {
                    strategy: elementData.strategy,
                    element_type: elementData.element_type,
                    data: {
                        points: elementData.data.points,
                        color: elementData.data.color || '#df4b26',
                        strokeWidth: elementData.data.strokeWidth || 2
                    }
                };
            }
            console.log('Sending payload to server:', payload);
            return api.post('/api/maps/elements/', payload);
        } catch (error) {
            console.error('Error preparing element data:', error);
            throw error;
        }
    },
    update: (id, data) => api.put(`/api/maps/elements/${id}/`, data),
    delete: (id) => api.delete(`/api/maps/elements/${id}/`),
};

export const authService = {
    getCSRFToken: async () => {
        try {
            const response = await api.get('/api/auth/csrf/');
            return response.data.csrfToken;
        } catch (error) {
            console.error('Failed to get CSRF token:', error);
            throw error;
        }
    },

    login: async (username, password) => {
        try {
            const response = await api.post('/api/auth/login/', {
                username,
                password
            });
            return response; // Return the response with user data
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    },

    logout: async () => {
        try {
            await api.post('/api/auth/logout/');
            // Don't need to remove token since we're using sessions
        } catch (error) {
            console.error('Logout failed:', error);
            throw error;
        }
    },

    checkAuth: async () => {
        try {
            // This endpoint checks the session cookie and returns auth status
            // The session cookie (sessionid) is automatically sent via withCredentials: true
            const response = await api.get('/api/auth/check/');
            console.log('checkAuth response status:', response.status);
            console.log('checkAuth response data:', response.data);
            return response;
        } catch (error) {
            console.error('Auth check failed:', error);
            console.error('Error response:', error.response?.data);
            throw error;
        }
    },

    signup: async (data) => {
        try {
            const response = await api.post('/api/auth/signup/', {
                username: data.username,
                email: data.email,
                password1: data.password1,
                password2: data.password2
            });
            return response.data;
        } catch (error) {
            console.error('Signup failed:', error);
            throw error;
        }
    },

    verifyEmail: async (key) => {
        try {
            const response = await api.post('/api/auth/verify-email/', { key });
            return response.data;
        } catch (error) {
            console.error('Email verification failed:', error);
            throw error;
        }
    },

    resendVerificationEmail: () =>
        api.post('/api/auth/resend-verification-email/'),

    getMe: () => api.get('/api/me/'),
    patchMe: (data) => api.patch('/api/me/', data),
    changePassword: (currentPassword, newPassword, newPasswordConfirm) =>
        api.post('/api/auth/change-password/', {
            current_password: currentPassword,
            new_password: newPassword,
            new_password_confirm: newPasswordConfirm,
        }),
    checkUsernameAvailability: (username) =>
        api.get('/api/auth/check-username/', { params: { username: username || '' } }),
    changeUsername: (newUsername, currentPassword) =>
        api.post('/api/auth/change-username/', {
            new_username: newUsername,
            current_password: currentPassword,
        }),
    revokeOtherSessions: () =>
        api.post('/api/auth/revoke-other-sessions/'),
};


export const operatorService = {
    getAll: (side) => api.get('/api/maps/operators/', { params: { side } }),
    getById: (id) => api.get(`/api/maps/operators/${id}/`),
};

export const geoguesserService = {
    // Maps
    getMaps: (gameId, options = {}) => {
        const params = {};
        if (gameId !== undefined && gameId !== null && gameId !== '') {
            params.game_id = gameId;
        }
        if (options.includeInactive) {
            params.include_inactive = 'true';
        }
        return api.get('/api/geoguesser/maps/', { params });
    },
    getMapById: (id) => api.get(`/api/geoguesser/maps/${id}/`),
    createMap: (data) => api.post('/api/geoguesser/maps/', data),
    updateMap: (id, data) => api.put(`/api/geoguesser/maps/${id}/`, data),
    patchMap: (id, data) => api.patch(`/api/geoguesser/maps/${id}/`, data),
    deleteMap: (id) => api.delete(`/api/geoguesser/maps/${id}/`),

    // Map migration wizard draft (staff-only opaque JSON blob stored per-map).
    // GET returns { data, updated_at } or resolves with status 204 when no
    // draft exists; PUT upserts, DELETE clears.
    getMigrationDraft: (mapId) =>
        api.get(`/api/geoguesser/maps/${mapId}/migration-draft/`),
    saveMigrationDraft: (mapId, data) =>
        api.put(`/api/geoguesser/maps/${mapId}/migration-draft/`, { data }),
    deleteMigrationDraft: (mapId) =>
        api.delete(`/api/geoguesser/maps/${mapId}/migration-draft/`),

    // Floors (Map → Floor → Level hierarchy)
    getFloors: (mapId) => {
        const params = mapId ? { map_id: mapId } : {};
        return api.get('/api/geoguesser/floors/', { params });
    },
    getFloorById: (id) => api.get(`/api/geoguesser/floors/${id}/`),
    createFloor: (data) => api.post('/api/geoguesser/floors/', data),
    updateFloor: (id, data) => api.patch(`/api/geoguesser/floors/${id}/`, data),
    deleteFloor: (id) => api.delete(`/api/geoguesser/floors/${id}/`),

    // Levels
    getLevels: (mapId, difficulty, showAnswer = false) => {
        const params = {};
        if (mapId) params.map_id = mapId;
        if (difficulty) params.difficulty = difficulty;
        if (showAnswer) params.show_answer = 'true';
        return api.get('/api/geoguesser/levels/', { params });
    },
    getLevelById: (id, showAnswer = false) => {
        const params = showAnswer ? { show_answer: 'true' } : {};
        return api.get(`/api/geoguesser/levels/${id}/`, { params });
    },
    createLevel: (data) => api.post('/api/geoguesser/levels/', data),
    updateLevel: (id, data) => api.patch(`/api/geoguesser/levels/${id}/`, data),
    deleteLevel: (id) => api.delete(`/api/geoguesser/levels/${id}/`),

    // Game Modes
    getGameModes: (params) => api.get('/api/geoguesser/game-modes/', { params }),
    getGameModeById: (id) => api.get(`/api/geoguesser/game-modes/${id}/`),
    createGameMode: (data) => api.post('/api/geoguesser/game-modes/', data),
    updateGameMode: (id, data) => api.put(`/api/geoguesser/game-modes/${id}/`, data),
    deleteGameMode: (id) => api.delete(`/api/geoguesser/game-modes/${id}/`),

    // Saved playlists (custom mode)
    getSavedPlaylists: () => api.get('/api/geoguesser/saved-playlists/'),
    getSavedPlaylistById: (id) => api.get(`/api/geoguesser/saved-playlists/${id}/`),
    createSavedPlaylist: (data) => api.post('/api/geoguesser/saved-playlists/', data),
    updateSavedPlaylist: (id, data) => api.put(`/api/geoguesser/saved-playlists/${id}/`, data),
    deleteSavedPlaylist: (id) => api.delete(`/api/geoguesser/saved-playlists/${id}/`),

    // Game Sessions
    getDailyChallengeStatus: (gameId) => api.get(`/api/geoguesser/games/${gameId}/daily-challenge-status/`),
    getDailyChallengeConfig: () => api.get('/api/geoguesser/daily-challenge-config/'),
    updateDailyChallengeConfig: (data) => api.patch('/api/geoguesser/daily-challenge-config/', data),
    getDailyChallengeGameConfigs: (gameId) => api.get('/api/geoguesser/daily-challenge-game-config/', { params: gameId ? { game_id: gameId } : {} }),
    getDailyChallengeGameConfig: (id) => api.get(`/api/geoguesser/daily-challenge-game-config/${id}/`),
    createDailyChallengeGameConfig: (data) => api.post('/api/geoguesser/daily-challenge-game-config/', data),
    updateDailyChallengeGameConfig: (id, data) => api.patch(`/api/geoguesser/daily-challenge-game-config/${id}/`, data),
    deleteDailyChallengeGameConfig: (id) => api.delete(`/api/geoguesser/daily-challenge-game-config/${id}/`),
    getScheduledDailyChallenges: (gameId) => api.get('/api/geoguesser/scheduled-daily-challenges/', { params: gameId ? { game_id: gameId } : {} }),
    getScheduledDailyChallenge: (id) => api.get(`/api/geoguesser/scheduled-daily-challenges/${id}/`),
    createScheduledDailyChallenge: (data) => api.post('/api/geoguesser/scheduled-daily-challenges/', data),
    updateScheduledDailyChallenge: (id, data) => api.patch(`/api/geoguesser/scheduled-daily-challenges/${id}/`, data),
    deleteScheduledDailyChallenge: (id) => api.delete(`/api/geoguesser/scheduled-daily-challenges/${id}/`),

    getSessions: (status, mySessions) => {
        const params = {};
        if (status) params.status = status;
        if (mySessions) params.my_sessions = 'true';
        return api.get('/api/geoguesser/sessions/', { params });
    },
    getSessionByCode: (code) => api.get('/api/geoguesser/sessions/by_code/', { params: { code } }),
    getSessionById: (id) => api.get(`/api/geoguesser/sessions/${id}/`),
    createSession: (data) => api.post('/api/geoguesser/sessions/', data),
    joinSession: (id, payload = {}) => api.post(`/api/geoguesser/sessions/${id}/join/`, payload),
    kickPlayer: (sessionId, { player_key }) =>
        api.post(`/api/geoguesser/sessions/${sessionId}/kick-player/`, { player_key }),
    inviteUser: (sessionId, { username }) => api.post(`/api/geoguesser/sessions/${sessionId}/invite-user/`, { username }),
    leaveSession: (id) => api.post(`/api/geoguesser/sessions/${id}/leave/`),
    startSession: (id) => api.post(`/api/geoguesser/sessions/${id}/start/`),
    getCurrentLevel: (id, params = {}) =>
        api.get(`/api/geoguesser/sessions/${id}/current_level/`, { params }),
    getGuessAssets: (id) => api.get(`/api/geoguesser/sessions/${id}/guess_assets/`),
    checkCompletion: (id) => api.post(`/api/geoguesser/sessions/${id}/check_completion/`),
    advanceSession: (id) => api.post(`/api/geoguesser/sessions/${id}/advance/`),
    getResults: (id) => api.get(`/api/geoguesser/sessions/${id}/results/`),
    claimRanked: (id) => api.post(`/api/geoguesser/sessions/${id}/claim_ranked/`),
    getLeaderboards: (params) => api.get('/api/geoguesser/sessions/leaderboards/', { params }),
    getSessionsBulk: (ids) => api.post('/api/geoguesser/sessions/bulk/', { ids }),
    getProfileSummary: (username) => api.get('/api/geoguesser/sessions/profile_summary/', { params: { username } }),

    // Guesses
    getGuesses: (sessionId) => api.get('/api/geoguesser/guesses/', { params: { session_id: sessionId } }),
    submitGuess: (data) => api.post('/api/geoguesser/guesses/', data),

    // Level Submissions (public submit + admin review)
    submitLevelSubmission: (formData) => api.post('/api/geoguesser/level-submissions/', formData),
    listLevelSubmissions: (params) => api.get('/api/geoguesser/level-submissions/', { params }),
    updateLevelSubmission: (id, data) => api.patch(`/api/geoguesser/level-submissions/${id}/`, data),
    approveLevelSubmission: (id) => api.post(`/api/geoguesser/level-submissions/${id}/approve/`),
    rejectLevelSubmission: (id) => api.post(`/api/geoguesser/level-submissions/${id}/reject/`),
};

export const deprecationService = {
    list: () => api.get('/api/deprecations/'),
    listKeys: () => api.get('/api/deprecations/keys/'),
    detail: (modelLabel, pk) => api.get(`/api/deprecations/${modelLabel}/${pk}/`),
    migrate: (modelLabel, pk, payload) =>
        api.post(`/api/deprecations/${modelLabel}/${pk}/migrate/`, payload || {}),
};

export const geoDataJobService = {
    list: (params = {}) => api.get('/api/geoguesser/data-jobs/', { params }),
    get: (id) => api.get(`/api/geoguesser/data-jobs/${id}/`),
    startExport: (payload = {}) =>
        api.post('/api/geoguesser/data-jobs/export/', payload),
    stage: ({ file, conflict_mode = 'insert_only' }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('conflict_mode', conflict_mode);
        return api.post('/api/geoguesser/data-jobs/stage/', formData);
    },
    getPreview: (id) => api.get(`/api/geoguesser/data-jobs/${id}/preview/`),
    updateMapping: (id, { decisions, conflict_mode } = {}) => {
        const body = {};
        if (decisions !== undefined) body.decisions = decisions;
        if (conflict_mode !== undefined) body.conflict_mode = conflict_mode;
        return api.patch(`/api/geoguesser/data-jobs/${id}/mapping/`, body);
    },
    apply: (id, { conflict_mode } = {}) =>
        api.post(
            `/api/geoguesser/data-jobs/${id}/apply/`,
            conflict_mode ? { conflict_mode } : {},
        ),
    discard: (id) => api.delete(`/api/geoguesser/data-jobs/${id}/discard/`),
    getExistingContent: (params = {}) =>
        api.get('/api/geoguesser/data-jobs/existing-content/', { params }),
};

/** Public badge catalog (no admin). */
export const badgesService = {
    getCatalog: () => api.get('/api/badges/catalog/'),
};

export const adminService = {
    getStats: () => api.get('/api/admin/stats/'),
    getGeoguesserStats: () => api.get('/api/admin/geoguesser-stats/'),
    getRecentActions: (limit = 20) => api.get('/api/admin/recent-actions/', { params: { limit } }),
    getCacheStats: () => api.get('/api/cache/stats/'),
    resetCacheStats: () => api.post('/api/cache/stats/reset/'),
    flushCache: () => api.post('/api/cache/flush/'),
    getBadges: () => api.get('/api/admin/badges/'),
    getBadgeAwards: (params) => api.get('/api/admin/badge-awards/', { params }),
};

export const feedbackService = {
    submit: (payload) => api.post('/api/feedback/feedback/', payload),
    list: (params) => api.get('/api/feedback/feedback/', { params }), // admin-only
    get: (id) => api.get(`/api/feedback/feedback/${id}/`), // admin-only
    update: (id, data) => api.patch(`/api/feedback/feedback/${id}/`, data), // admin-only
};

export const teamService = {
    // Basic CRUD
    getAll: (params = {}) => api.get('/api/teams/teams/', { params }),
    getById: (id, params = {}) => api.get(`/api/teams/teams/${id}/`, { params }),
    create: (data) => {
        // Handle FormData for logo upload
        if (data.logo instanceof File) {
            const formData = new FormData();
            formData.append('name', data.name);
            if (data.description) formData.append('description', data.description);
            if (data.logo) formData.append('logo', data.logo);
            return api.post('/api/teams/teams/', formData);
        }
        return api.post('/api/teams/teams/', data);
    },
    update: (id, data) => {
        // Handle FormData for logo upload
        if (data.logo instanceof File) {
            const formData = new FormData();
            if (data.name) formData.append('name', data.name);
            if (data.description !== undefined) formData.append('description', data.description);
            if (data.logo) formData.append('logo', data.logo);
            if (data.is_active !== undefined) formData.append('is_active', data.is_active);
            return api.patch(`/api/teams/teams/${id}/`, formData);
        }
        return api.patch(`/api/teams/teams/${id}/`, data);
    },
    delete: (id) => api.delete(`/api/teams/teams/${id}/`),

    // Team membership
    join: (id) => api.post(`/api/teams/teams/${id}/join/`),
    leave: (id) => api.post(`/api/teams/teams/${id}/leave/`),

    // Team members
    getMembers: (id, params = {}) => api.get(`/api/teams/teams/${id}/members/`, { params }),
    updateMemberRole: (teamId, userId, role) => api.post(
        `/api/teams/teams/${teamId}/update_member_role/`,
        { user_id: userId, role }
    ),
    removeMember: (teamId, userId) => api.post(
        `/api/teams/teams/${teamId}/remove_member/`,
        { user_id: userId }
    ),

    // User's teams
    getMyTeams: () => api.get('/api/teams/teams/my_teams/'),
    /** Staff only: teams for a given user (e.g. Add Participant dropdown). */
    getTeamsForUser: (userId) => api.get('/api/teams/teams/', { params: { user_id: userId } }),

    // Team join requests
    requestJoin: (teamId, message) => {
        const data = { team_id: teamId };
        if (message) data.message = message;
        return api.post('/api/teams/join-requests/', data);
    },
    getJoinRequests: (teamId, params = {}) => {
        const queryParams = { ...params };
        if (teamId) queryParams.team = teamId;
        return api.get('/api/teams/join-requests/', { params: queryParams });
    },
    getMyJoinRequests: (params = {}) => {
        const queryParams = { ...params };
        // The backend will filter by current user automatically
        return api.get('/api/teams/join-requests/', { params: queryParams });
    },
    approveJoinRequest: (requestId, reviewMessage) => api.post(
        `/api/teams/join-requests/${requestId}/approve/`,
        { status: 'approved', review_message: reviewMessage || '' }
    ),
    rejectJoinRequest: (requestId, reviewMessage) => api.post(
        `/api/teams/join-requests/${requestId}/reject/`,
        { status: 'rejected', review_message: reviewMessage || '' }
    ),
    cancelJoinRequest: (requestId) => api.delete(`/api/teams/join-requests/${requestId}/`),

    // Team invitations
    createInvitation: (teamId, invitedUserIdentifier, message, expiresAt) => {
        const data = {
            team_id: teamId,
            invited_user: invitedUserIdentifier
        };
        if (message) data.message = message;
        if (expiresAt) data.expires_at = expiresAt;
        return api.post('/api/teams/invitations/', data);
    },
    getInvitations: (teamId, params = {}) => {
        const queryParams = { ...params };
        if (teamId) queryParams.team = teamId;
        return api.get('/api/teams/invitations/', { params: queryParams });
    },
    getMyInvitations: (params = {}) => {
        const queryParams = { ...params };
        // The backend will filter by current user automatically
        return api.get('/api/teams/invitations/', { params: queryParams });
    },
    acceptInvitation: (invitationId) => api.post(
        `/api/teams/invitations/${invitationId}/accept/`,
        { status: 'accepted' }
    ),
    declineInvitation: (invitationId) => api.post(
        `/api/teams/invitations/${invitationId}/decline/`,
        { status: 'declined' }
    ),
    cancelInvitation: (invitationId) => api.delete(`/api/teams/invitations/${invitationId}/`),

    // Team pending requests/invitations (for admins)
    getPendingRequests: (teamId, params = {}) => api.get(`/api/teams/teams/${teamId}/pending-requests/`, { params }),
    getPendingInvitations: (teamId, params = {}) => api.get(`/api/teams/teams/${teamId}/pending-invitations/`, { params }),
};

// Notification Service
export const notificationService = {
    // Get notifications
    getNotifications: (params = {}) => api.get('/api/notifications/', { params }),

    // Get unread count
    getUnreadCount: (params = {}) => api.get('/api/notifications/unread-count/', { params }),

    // Poll for new notifications
    pollNotifications: (lastCheck) => {
        const params = lastCheck ? { last_check: lastCheck } : {};
        return api.get('/api/notifications/poll/', { params });
    },

    // Mark as read
    markAsRead: (notificationId) => api.post(`/api/notifications/${notificationId}/mark-as-read/`),

    // Mark all as read
    markAllAsRead: () => api.post('/api/notifications/mark-all-as-read/'),
};

export const tournamentService = {
    list: (params = {}) => api.get('/api/tournament-tools/tournaments/', { params }),
    getById: (publicId) => api.get(`/api/tournament-tools/tournaments/${publicId}/`),
    create: (data) => api.post('/api/tournament-tools/tournaments/', data),
    update: (publicId, data) => api.patch(`/api/tournament-tools/tournaments/${publicId}/`, data),
    createInvite: (publicId, data) => api.post(`/api/tournament-tools/tournaments/${publicId}/invites/`, data),
    acceptInvite: (token) => api.post('/api/tournament-tools/invites/accept/', { token }),
    register: (publicId, payload = {}) =>
        api.post(`/api/tournament-tools/tournaments/${publicId}/register/`, payload),
    announce: (publicId, payload) => api.post(`/api/tournament-tools/tournaments/${publicId}/announce/`, payload),
    getStages: (publicId) => api.get(`/api/tournament-tools/tournaments/${publicId}/stages/`),
    createStage: (publicId, payload) => api.post(`/api/tournament-tools/tournaments/${publicId}/stages/`, payload),
    activateStage: (publicId, stageId) =>
        api.post(`/api/tournament-tools/tournaments/${publicId}/stages/${stageId}/activate/`),
    listInvites: () => api.get('/api/tournament-tools/invites/'),
    getRegistration: (publicId) => api.get(`/api/tournament-tools/tournaments/${publicId}/registration/`),
    checkIn: (publicId) => api.post(`/api/tournament-tools/tournaments/${publicId}/check-in/`),
    unregister: (publicId) => api.post(`/api/tournament-tools/tournaments/${publicId}/unregister/`),
    getParticipants: (publicId) => api.get(`/api/tournament-tools/tournaments/${publicId}/participants/`),
    addParticipant: (publicId, payload) =>
        api.post(`/api/tournament-tools/tournaments/${publicId}/participants/admin-add/`, payload),
    removeParticipant: (publicId, participantId) =>
        api.post(`/api/tournament-tools/tournaments/${publicId}/participants/${participantId}/remove/`, {}),
    resolveInvite: (params = {}) => api.get('/api/tournament-tools/invites/resolve/', { params }),
    cancel: (publicId) => api.post(`/api/tournament-tools/tournaments/${publicId}/cancel/`),
    updateStatus: (publicId, status) =>
        api.post(`/api/tournament-tools/tournaments/${publicId}/status/`, { status }),
    getBracket: (publicId) => api.get(`/api/tournament-tools/tournaments/${publicId}/bracket/`),
    generateBracket: (publicId) => api.post(`/api/tournament-tools/tournaments/${publicId}/bracket/generate/`),
    seedBracket: (publicId, payload) => api.post(`/api/tournament-tools/tournaments/${publicId}/bracket/seed/`, payload),
    getTimeline: (publicId) => api.get(`/api/tournament-tools/tournaments/${publicId}/timeline/`),
    reportMatch: (publicId, matchId, payload) =>
        api.post(`/api/tournament-tools/tournaments/${publicId}/matches/${matchId}/report/`, payload),
    acceptParticipantInvite: (tournamentPublicId, participantId) =>
        api.post(`/api/tournament-tools/tournaments/${tournamentPublicId}/participants/${participantId}/accept-invitation/`),
    declineParticipantInvite: (tournamentPublicId, participantId) =>
        api.post(`/api/tournament-tools/tournaments/${tournamentPublicId}/participants/${participantId}/decline-invitation/`),
};

export const userService = {
    // Autocomplete search for users
    autocomplete: (query, limit = 10) => {
        const params = { q: query, limit };
        return api.get('/api/users/autocomplete/', { params });
    },
};

/** DRF may return a bare array or `{ results: [...] }` when paginated. */
export function normalizeApiList(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
}

export default api;
